import { Container } from '@Calix-Commerce/design-system/layout';
import { getI18NText } from 'I18N';
import { CheckoutFooter } from 'components/Footer';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState, useRecoilState } from 'recoil';
import {
  currentQuoteState,
  loadingState,
  loggedInAccountState,
  notificationState,
  purchaseOrderFileState,
} from 'state/atoms';
import { readCheckoutSavedData, saveCheckoutDataInLocalStorage } from 'utils/helpers/checkout';
import { useAppNavigation } from 'utils/hooks';
import { useDuplicatePoNumberLoad } from 'utils/hooks/useCheckout';
import { validateCheckoutBilling } from './CheckoutBillingValidations';
import { BillingInfoForm } from './subcomponents/BillingInfoForm';
import { PurchaseInfoForm } from './subcomponents/PurchaseInfoForm';
import { CsapProgram } from './subcomponents/CsapProgram';
import { CSAP_ENROLLMENT_OPT_OUT, FETCH_POLICY } from 'utils/constants';
import { Address } from 'types';
import { CheckoutStepper } from 'components/stepper';
import { withQuoteLoad } from 'hocs';
import { withCheckoutPermissions } from 'hocs/withCheckoutPermissions';
import { withCheckoutShippingConfigured } from 'hocs/withCheckoutShippingConfigured';
import { CURRENT_CHECKOUT_STEP } from 'utils/constants/checkout';
import { CheckoutTitle } from 'components/Checkout';
import { storageSelectedAccountSelector } from 'state/selectors';
import { withCheckoutAccessible } from 'hocs/withCheckoutAccessible';
import { FileMissingModal } from 'components/modals';
import { getCsapEligibleParts } from './helpers';

const CheckoutBillingPage = () => {
  const { transactionId } = useParams();
  const { accountId } = useRecoilValue(storageSelectedAccountSelector);
  const [currentQuoteData, setCurrentQuoteState] = useRecoilState(currentQuoteState);
  const setLoadingState = useSetRecoilState(loadingState);
  const { redirectToCheckoutShipping, redirectToCheckoutReview } = useAppNavigation();
  const [address, setAddress] = useState<Address>();
  const [leasingAgent, setLeasingAgent] = useState(false);
  const [poNumber, setPoNumber] = useState('');
  const [csapEnrolled, setCsapEnrolled] = useState('');
  const [isValidPoNumber, setIsValidPoNumber] = useState(false);
  const [isPoNumberRequestInProgress, setIsPoNumberRequestInProgress] = useState(false);
  const [navigateAfterValidPoNumber, setNavigateAfterValidPoNumber] = useState(false);
  const [poNumberErrors, setPoNumberErrors] = useState([]);
  const [poFile, setPoFile] = useRecoilState(purchaseOrderFileState);
  const [billDataToPersist, setBillDataToPersist] = useState({});
  const [showPoModal, setShowPoModal] = useState(false);
  const [errors, setErrors] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
    poNumber: '',
    poFile: '',
    csapEnrolled: '',
  });
  const { duplicatePoNumberRequest } = useDuplicatePoNumberLoad();
  const { businessCountry } = useRecoilValue(loggedInAccountState);
  const setNotification = useSetRecoilState(notificationState);
  const csapPricePerGroup = useMemo(() => {
    const checkoutData = readCheckoutSavedData(transactionId);

    const csapEligibility = getCsapEligibleParts({
      currentQuoteData,
      checkoutData,
    });

    if (
      csapEnrolled !== CSAP_ENROLLMENT_OPT_OUT &&
      !Object.keys(csapEligibility.csapRecords).length
    ) {
      setCsapEnrolled(CSAP_ENROLLMENT_OPT_OUT);
    }

    return csapEligibility;
  }, [csapEnrolled, currentQuoteData, transactionId]);

  const showCsapSection = Object.keys(csapPricePerGroup.csapRecords).length > 0;

  const validateDuplicatePoNumber = (newPoNumber, relatedTargetId) => {
    const isNextClicked = relatedTargetId === 'checkout-page-next';
    const isBackClicked = relatedTargetId === 'checkout-page-back';

    if (isBackClicked) {
      redirectToCheckoutShipping();
      return;
    }

    if (isNextClicked) {
      setLoadingState(true);
    }

    setIsPoNumberRequestInProgress(true);

    duplicatePoNumberRequest({
      onCompleted: (poNumberResponse) => {
        setLoadingState(false);
        setIsPoNumberRequestInProgress(false);

        const duplicatePoNumbers = poNumberResponse.getAccount.findDuplicatedPoNumber || [];

        setPoNumberErrors(duplicatePoNumbers);
        setIsValidPoNumber(duplicatePoNumbers.length === 0);

        if (
          isNextClicked &&
          !duplicatePoNumbers.length &&
          isBillingPageValid({ allowDuplicatePoNumber: false })
        ) {
          redirectToCheckoutReview({ file: poFile });
        }
      },
      onError: (poNumberError) => {
        setLoadingState(false);
        setIsPoNumberRequestInProgress(false);
        setNotification({
          text: poNumberError.toString(),
          show: true,
          type: 'error',
          timeout: null,
        });
      },
      variables: {
        accountId,
        poNumber: newPoNumber,
      },
      fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
    });
  };

  const saveBillingDataOnCurrentQuote = useCallback(() => {
    setCurrentQuoteState((currentQuoteData) => ({
      ...currentQuoteData,
      billingAddress: {
        ...(address ?? ({} as Address)),
        id: address?.addressId,
      },
      lesingAgent: leasingAgent,
      poNumber,
      csapEnrolled,
    }));
  }, [setCurrentQuoteState, poNumber, csapEnrolled, leasingAgent, address]);

  const backNavigationConfig = {
    navigate: async () => redirectToCheckoutShipping(),
  };

  const isBillingPageValid = ({
    allowDuplicatePoNumber,
    skipElementFocus,
  }: {
    allowDuplicatePoNumber: boolean;
    skipElementFocus?: boolean;
  }) => {
    const billingAddress = {
      id: address?.addressId,
      name: address?.name,
      isFavorite: false,
      address1: address?.address1,
      address2: address?.address2,
      city: address?.city,
      state: address?.state,
      zipcode: address?.zipcode,
      country: address?.country,
      searchAddress: undefined,
      lesingAgent: leasingAgent,
    };

    const { isAllDataValid, billingErrors, focusOnElement } = validateCheckoutBilling({
      billingAddress,
      poFile,
      poNumber,
      csapEnrolled,
    });

    if (focusOnElement && !skipElementFocus) {
      setTimeout(() => {
        const focusEl = document.getElementById(focusOnElement);

        focusEl &&
          focusEl.scrollIntoView({
            behavior: 'smooth',
          });
      }, 0);
    }

    if (isAllDataValid && (isValidPoNumber || allowDuplicatePoNumber)) {
      const finalBillingData = {
        billingAddress,
        poNumber,
        transactionId,
        csapEnrolled,
        lesingAgent: leasingAgent,
      };

      setBillDataToPersist(finalBillingData);
      saveCheckoutDataInLocalStorage({
        ...finalBillingData,
        transactionId,
      });
      saveBillingDataOnCurrentQuote();

      return true;
    }

    setErrors(billingErrors);

    if (!isAllDataValid) {
      displayErrorMessages(billingErrors);
    }

    return false;
  };

  const displayErrorMessages = (billingErrors) => {
    if (billingErrors.poLength) {
      setNotification({
        text: getI18NText('PO_ATTACHMENT_SIZE_ERROR_MESSAGE'),
        show: true,
        type: 'error',
        timeout: null,
      });
    } else {
      setNotification({
        text: getI18NText('EMPTY_FIELDS_ERROR'),
        show: true,
        type: 'error',
        timeout: null,
      });
    }
  };

  const nextNavigationValidation = async () => {
    if (isPoNumberRequestInProgress) {
      setLoadingState(true);
      setNavigateAfterValidPoNumber(true);
      return Promise.reject('Po number validation in progress');
    }

    if (isBillingPageValid({ allowDuplicatePoNumber: false })) {
      return Promise.resolve();
    }

    return Promise.reject('Validations fails for billing');
  };

  const nextNavigationConfig = {
    navigate: async (): Promise<void> => {
      // Don't redirect if there was a file uploaded (no longer present)
      if (poFile?.fileName && !poFile?.fileData) {
        // Only show the modal once
        saveCheckoutDataInLocalStorage({
          poFileAttachmentName: undefined,
          transactionId,
        });
        setPoFile({});
        setShowPoModal(true);
      } else {
        redirectToCheckoutReview({ file: poFile });
      }
    },
    validate: nextNavigationValidation,
  };

  useEffect(() => {
    if (
      !isPoNumberRequestInProgress &&
      navigateAfterValidPoNumber &&
      isBillingPageValid({ allowDuplicatePoNumber: false, skipElementFocus: true })
    ) {
      redirectToCheckoutReview({ file: poFile });
    }
  }, [isPoNumberRequestInProgress, navigateAfterValidPoNumber]);

  useEffect(() => {
    const savedCheckoutData = readCheckoutSavedData(transactionId);
    const { billingAddress } = savedCheckoutData;

    if (billingAddress) {
      setAddress({
        addressId: billingAddress.id,
        name: billingAddress.name,
        address1: billingAddress.address1,
        address2: billingAddress.address2,
        city: billingAddress.city,
        state: billingAddress.state,
        zipcode: billingAddress.zipcode,
        country: billingAddress.country,
      });

      setLeasingAgent(billingAddress.lesingAgent);
    } else {
      setAddress({
        ...(address || ({} as Address)),
        country: businessCountry,
      });
    }

    setCsapEnrolled(savedCheckoutData.csapEnrolled || '');
    setPoNumber(savedCheckoutData.poNumber || '');
    setIsValidPoNumber(Boolean(savedCheckoutData.poNumber));
    if (!poFile?.fileName && savedCheckoutData.poFileAttachmentName) {
      setPoFile({ fileName: savedCheckoutData.poFileAttachmentName });
    }
  }, [businessCountry]);

  return (
    <Container>
      <CheckoutStepper
        currentStep={CURRENT_CHECKOUT_STEP.BILLING}
        validate={nextNavigationValidation}
      />
      <CheckoutTitle id="billing-page-header">{getI18NText('BILLING_INFO')}</CheckoutTitle>
      <FileMissingModal
        isOpen={showPoModal}
        onConfirmation={() => {
          setShowPoModal(false);
          redirectToCheckoutReview({ file: poFile });
        }}
        onCancel={() => {
          setShowPoModal(false);
        }}
      />
      <BillingInfoForm
        address={address}
        onAddressChange={(temp) => {
          setAddress(temp);
        }}
        leasingAgent={leasingAgent}
        onLeasingAgentChange={setLeasingAgent}
        errors={errors}
        setErrors={setErrors}
      />
      <CheckoutTitle id="billing-page-po-number-heading">
        {getI18NText('PURCHASE_ORDER_INFO')}
      </CheckoutTitle>
      <PurchaseInfoForm
        poNumber={poNumber}
        onPoNumberChange={setPoNumber}
        onValidPoNumberChange={setIsValidPoNumber}
        onPoFileChange={(file) => {
          saveCheckoutDataInLocalStorage({
            poFileAttachmentName: file?.fileName || undefined,
            transactionId,
          });

          setPoFile(file);
        }}
        poFile={poFile}
        poNumberErrors={poNumberErrors}
        setPoNumberErrors={setPoNumberErrors}
        errors={errors}
        setErrors={setErrors}
        onConfirmationCallback={() => {
          setIsValidPoNumber(true);
          saveCheckoutDataInLocalStorage({
            ...billDataToPersist,
            transactionId,
          });
          saveBillingDataOnCurrentQuote();

          if (isBillingPageValid({ allowDuplicatePoNumber: true })) {
            nextNavigationConfig.navigate();
          }
        }}
        handleBlurPoNumber={(newPoNumber, relatedTargetId) => {
          validateDuplicatePoNumber(newPoNumber, relatedTargetId);
        }}
      />
      {showCsapSection && (
        <>
          <CheckoutTitle id="billing-page-csap-program-heading">
            {getI18NText('CSAP_SECTION_HEADER')}
          </CheckoutTitle>
          <CsapProgram
            setErrors={setErrors}
            csapEnrolled={csapEnrolled}
            setCsapEnrolled={setCsapEnrolled}
            csapPricePerGroup={csapPricePerGroup.csapRecords}
            csapEnrolledValidation={errors?.csapEnrolled}
          />
        </>
      )}
      <CheckoutFooter
        backNavigationConfig={backNavigationConfig}
        nextNavigationConfig={nextNavigationConfig}
      />
    </Container>
  );
};

export const CheckoutBilling = withCheckoutPermissions(
  withQuoteLoad(withCheckoutAccessible(withCheckoutShippingConfigured(CheckoutBillingPage)))
);
